import { formatMoney, type Currency, type Locale } from '@worki/shared';

const intlLocale = (locale: Locale) => (locale === 'he' ? 'he-IL' : 'en-GB');

export function money(amount: number, currency: Currency, locale: Locale): string {
  return formatMoney(amount, currency, intlLocale(locale));
}

/**
 * INV-006 — the name of a month, 1-12, for choosing one rather than reading a
 * date. Taken off a real date in that month so the Hebrew form is the one the
 * locale itself uses; the year is arbitrary and never shown.
 */
export function monthName(month: number, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { month: 'long' }).format(new Date(Date.UTC(2001, month - 1, 15)));
}

export function formatDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

export function formatDateTime(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Just the clock face — for a list where every row is already today. */
export function formatTime(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** "לפני 3 שעות" / "in 2 days" — Intl handles the wording per locale. */
export function formatRelative(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';

  const diffMs = new Date(iso).getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });

  /**
   * CLDR's Hebrew data spells the singular and dual out and then repeats the
   * count in brackets — Intl returns literally "לפני שעה (1)" and
   * "לפני שעתיים (2)". The words are already correct Hebrew; the bracket is a
   * disambiguation artifact that no Israeli would write, and it showed up on
   * every notification and activity line from one hour or two days old.
   */
  const clean = (text: string) => text.replace(/\s*\(\d+\)\s*$/, '');

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 365 * 86_400_000],
    ['month', 30 * 86_400_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (Math.abs(diffMs) >= ms) return clean(rtf.format(Math.round(diffMs / ms), unit));
  }
  return clean(rtf.format(Math.round(diffMs / 1000), 'second'));
}

/** Seconds of cumulative view time -> "4:07" (QUO-010). */
export function formatDuration(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0:00';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** `<input type="datetime-local">` wants local wall-clock, not UTC. */
export function toLocalInputValue(iso: string): string {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function fromLocalInputValue(value: string): string {
  return new Date(value).toISOString();
}
